import React from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";

const STEP_LABEL = {
  email: "Email",
  linkedin: "LinkedIn",
  call: "Call",
  custom_task: "Task",
  deep_research: "Deep research"
};

// Plain-text excerpt of a step body for the peek: strips tags, collapses
// whitespace, clips to one short paragraph.
const excerpt = (html, max = 220) => {
  const text = String(html || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
};

/**
 * Sequence artifact card. Peeks the first step (channel, subject, opening
 * lines) so the user sees the actual copy in the thread, fading out under a
 * floating CTA that opens the docked preview panel.
 */
const LivePreviewBlock = observer(({ block }) => {
  const building = block.status === "building";
  const failed = block.status === "failed";
  const steps = block.steps || [];
  const stepCount = steps.length;
  const first = steps[0];
  const firstBody = first ? excerpt(first.body || first.message || first.notes) : "";
  const showPeek = !failed && first && (first.subject || firstBody);
  const open = () => copilotStore.openPreviewPanel(block.id);
  const ctaLabel = building
    ? "Watch live"
    : `Review ${stepCount} step${stepCount === 1 ? "" : "s"}`;

  return (
    <div className={`copilotLiveCard status-${block.status || "building"}`} onClick={open}>
      <div className="copilotLiveCard__head">
        <div className="copilotLiveCard__icon">
          {building ? (
            <span className="copilotLiveCard__liveDot" />
          ) : (
            <i className="material-icons-outlined">
              {failed ? "error_outline" : "check_circle"}
            </i>
          )}
        </div>
        <div className="copilotLiveCard__meta">
          <div className="copilotLiveCard__title">{block.title}</div>
          <div className="copilotLiveCard__sub">
            {building
              ? `Building live · ${stepCount} step${stepCount === 1 ? "" : "s"} so far`
              : failed
              ? "Creation failed"
              : `Sequence created · ${stepCount} step${stepCount === 1 ? "" : "s"}`}
          </div>
        </div>
        {!showPeek && (
          <div className="copilotLiveCard__open">
            <span>{building ? "Watch live" : "View"}</span>
            <i className="material-icons-outlined">chevron_right</i>
          </div>
        )}
      </div>

      {showPeek && (
        <div className="copilotLiveCard__peek">
          <div className="copilotLiveCard__step">
            <div className="copilotLiveCard__stepHead">
              <span className="copilotLiveCard__stepBadge">
                Step 1 · {STEP_LABEL[first.type] || "Step"} · Day 1
              </span>
              {first.aiGenerated && <span className="copilotLiveCard__ai">AI written</span>}
            </div>
            {first.subject && (
              <div className="copilotLiveCard__subject">{first.subject}</div>
            )}
            {firstBody && <div className="copilotLiveCard__body">{firstBody}</div>}
            {!firstBody && first.type === "deep_research" && (
              <div className="copilotLiveCard__body">Written per contact at send time.</div>
            )}
          </div>
          <div className="copilotLiveCard__fade" />
          <button
            type="button"
            className="copilotLiveCard__cta"
            onClick={e => {
              e.stopPropagation();
              open();
            }}
          >
            <i className="material-icons-outlined">{building ? "visibility" : "send"}</i>
            {ctaLabel}
          </button>
        </div>
      )}
    </div>
  );
});

export default LivePreviewBlock;
